import React, { useCallback, useState } from 'react';
import PromptDateDialog from '../../shared/promptDateDialog.component';
import UserExpirationGrid from './userExpirationGrid.component';
import Button from '../../shared/button.component';
import { useExpiration } from './useExpiration';
import { UserExpiration } from '../../shared/queries/users';
import { PureAlert as Alert } from '../../shared/alert.component';
import { useAlertState } from '../../shared/hooks/useAlertState';
import alertTypes from '../../shared/alertTypes';

type UserExpirationDataProps = {
  users: UserExpiration[],
  isLoading: boolean,
  canEditExpirationDate: boolean,
  submitUsers: (usersToUpdate: UserExpiration[]) => void
};

const UserExpirationData = ({
  users,
  isLoading,
  canEditExpirationDate,
  submitUsers,
}: UserExpirationDataProps) => {
  const [isPromptVisible, setIsPromptVisible] = useState<boolean>(false);

  const { alert, showAlert, hideAlert } = useAlertState();

  const {
    localUsers,
    editedUsers,
    selectedUsers,
    usersToUpdate,
    setSelectedUsers,
    updateUsers,
    revertEditedUsers,
    clearSelectedUsers,
  } = useExpiration(users);

  const disabledButtons = isLoading || editedUsers.length === 0;

  const togglePropmt = useCallback(() => setIsPromptVisible(!isPromptVisible), [isPromptVisible]);

  const handleOnConfirm = useCallback((value: string) => {
    togglePropmt();
    updateUsers(value);
  }, [togglePropmt, updateUsers]);

  const newDateForSelectedUsers = useCallback(() => {
    let errorMessage = '';

    if (selectedUsers.length === 0) {
      errorMessage = 'No users entry(s) are selected.';
    } else if (usersToUpdate.length === 0) {
      errorMessage = 'Please select at least one edited user entry.';
    }

    if (errorMessage) {
      showAlert(alertTypes.DANGER, errorMessage);
      return;
    }
    clearSelectedUsers();
    submitUsers(usersToUpdate);
  }, [selectedUsers, usersToUpdate, submitUsers, clearSelectedUsers, showAlert]);

  return (
    <>
      <PromptDateDialog
        title="Select expiration value"
        dismissText="Cancel"
        submitText="Set new expiration date"
        isVisible={isPromptVisible}
        onCancel={togglePropmt}
        onConfirm={handleOnConfirm}
      />
      {alert && (
        <Alert
          content={alert.content}
          type={alert.alertType}
          hideAlert={hideAlert}
          show={alert.show}
        />
      )}
      <UserExpirationGrid
        canModify={canEditExpirationDate}
        users={localUsers}
        selectedUsers={selectedUsers}
        editedUsers={editedUsers}
        handleSelect={setSelectedUsers}
        onClickNewDate={togglePropmt}
        isLoading={isLoading}
      >
        <div className="ml-auto">
          <>
            {editedUsers.length !== 0 && <span className="text-danger">{editedUsers.length} entries pending submissions</span>}
            <Button
              kind="secondary"
              className="a-mx-10"
              onClick={revertEditedUsers}
              disabled={disabledButtons}
            >
              Revert
            </Button>
          </>
          <Button
            onClick={newDateForSelectedUsers}
            disabled={disabledButtons}
          >
            Submit Selected
          </Button>
        </div>
      </UserExpirationGrid>
    </>
  );
};

export default UserExpirationData;
